// pages/DeliveryStatusPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getUrl } from "../utils/urls";
import GoogleMap from "../components/GoogleMap";
import BusDeliveryStatus from "./BusStatus";
import { Box, Typography, CircularProgress, Paper, Link } from "@mui/material";
import {
  Phone,
  AccessTime,
  Person,
  LocalShipping,
  CheckCircle,
  Pending,
} from "@mui/icons-material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import recordPageView from "../utils/recordPageView"; 
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const DeliveryStatusPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { id } = useParams();
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);

  useEffect(() => {
    const deviceInfo = {
      platform: navigator.platform,
      userAgent: navigator.userAgent,
    };

    // Call the reusable recordPageView function
    recordPageView({
      deliveryId: id,
      userId: "anonymous",
      deviceInfo,
      source: "sms-click",
    });

    const fetchStatusData = async () => {
      try {
        const currentUrl = window.location.href;
        console.log('fetching...')
        const response = await axios.post(`${getUrl("tracking")}`, {
          id,
          origin: currentUrl,
        });
        setStatusData(response.data);
      } catch (err) {
        setError("Failed to fetch delivery status");
      } finally {
        setLoading(false);
      }
    };

    fetchStatusData();
  }, [id]);

  const statusConfig = {
    completed: { label: "Toimitettu", icon: <CheckCircle color="success" /> },
    active: { label: "Odottaa kuljettajaa", icon: <Pending color="action" /> },
    ongoing: {
      label: "Kuljettaja löytynyt",
      icon: <LocalShipping color="primary" />,
    },
    picked: { label: "Noudettu", icon: <LocalShipping color="secondary" /> },
    pending: { label: "Odottaa kuljettajaa", icon: <Pending color="action" /> },
  };

  const renderStatusMessage = (status) =>
    statusConfig[status] || { label: "Status not available", icon: null };

  // Rotating advertisement messages
  const ads = [
    t("marketingTextEuro"),
    t("marketingTextLanding1"),
    t("marketingTextLanding2"),
    t("marketingTextLanding3"),
    t("marketingTextLanding4"),
  ];
  const [adHovered, setAdHovered] = useState(false);

  // Cycle through advertisement messages every 10 seconds
  useEffect(() => {
    let adInterval;
    if (!adHovered) {
      adInterval = setInterval(() => {
        setCurrentAdIndex((prevIndex) => (prevIndex + 1) % ads.length);
      }, 10000);
    }
    return () => clearInterval(adInterval);
  }, [ads.length, adHovered]);
  
  const { label, icon } = statusData ? renderStatusMessage(statusData.status) : { label: "", icon: null };
  
  const handleAdClick = () => {
    navigate(`/eurolla/${id}`, { state: { source: "eurolla-banner" } });
  };
  
  // Handle status change for bus delivery
  const handleBusStatusChange = async () => {
    const deliveryId = statusData.deliveryId;
    return new Promise(async (resolve, reject) => {
      try {
        setError(null);
        console.log('first', {
          id: deliveryId,
          status: 'picked',
        })
        // Call the API to mark delivery as picked
        const statusResponse = await axios.post(`${getUrl("updateDeliveryStatus")}`, {
          id: deliveryId,
          status: 'picked',
        });
        
        if (!statusResponse.data.success) {
          throw new Error(statusResponse.data.error || 'Paketin merkitseminen noudetuksi epäonnistui');
        }
        
        // Refetch the delivery status to reflect changes
        const currentUrl = window.location.href;
        const response = await axios.post(`${getUrl("tracking")}`, {
          id: deliveryId,
          origin: currentUrl,
        });
        
        setStatusData(response.data);
        resolve();
      } catch (err) {
        console.error("Error marking delivery as picked:", err);
        setError(err.message || "Paketin merkitseminen noudetuksi epäonnistui. Yritä uudelleen.");
        reject(err);
      }
    });
  };

  // Check if this is a bus transport delivery
  const isBusDelivery = statusData?.deliveryTransportType === 'bus-transport';

  return (
    <Box padding={2}>
      {/* Advertisement Section */}
      <Paper
  onClick={handleAdClick}
  onMouseEnter={() => setAdHovered(true)}
  onMouseLeave={() => setAdHovered(false)}
  elevation={adHovered ? 6 : 4}
  sx={{
    padding: 2,
    marginBottom: 2,
    background: adHovered 
      ? 'linear-gradient(45deg, #b2ebf2 30%, #80deea 90%)' 
      : 'linear-gradient(45deg, #e0f7fa 30%, #b2ebf2 90%)',
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: 2,
    height: 100, // Fixed height for the ad container
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
  }}
>
  {/* Optional: Add subtle animation indicator */}
  <Box 
    sx={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      height: '3px',
      width: `${(currentAdIndex / (ads.length - 1)) * 100}%`,
      backgroundColor: '#00796b',
      transition: 'width 0.5s ease-out',
    }}
  />
  
  <Box sx={{ 
    textAlign: "center", 
    color: "#00796b",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    zIndex: 1,
  }}>
    <Typography 
      variant="h6" 
      sx={{ 
        fontWeight: "bold",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        lineHeight: "1.2em",
        maxHeight: "2.4em", // Allows for approximately 2 lines of text
        transition: 'font-size 0.2s ease',
        fontSize: {
          xs: '0.9rem', // Smaller font on extra small screens
          sm: '1rem',   // Small screens
          md: '1.25rem' // Default for medium and up
        }
      }}
    >
      {ads[currentAdIndex]}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        marginTop: 1,
        fontWeight: 500,
        color: "#004d40",
      }}
    >
      {t('clickHere')}
      <ArrowForwardIcon 
        fontSize="small" 
        sx={{ 
          transform: adHovered ? 'translateX(3px)' : 'translateX(0)', 
          transition: 'transform 0.3s ease' 
        }} 
      />
    </Typography>
  </Box>
</Paper>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Render different views based on delivery type */}
          {isBusDelivery ? (
            // Bus Delivery Component
            <BusDeliveryStatus 
              statusData={statusData} 
              onStatusChange={handleBusStatusChange} 
            />
          ) : (
            // Standard Delivery View
            <>
              {/* Upper Paper with Basic Details and Courier Info */}
              <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t("deliveryStatus")}
                </Typography>
     
                <Box display="flex" alignItems="center" marginBottom={1}>
                  {icon}
                  <Typography variant="body2" sx={{ marginLeft: 1 }}>
                    <strong>{t("status")}:</strong> {label}
                  </Typography>
                </Box>
                {statusData.status !== 'completed' && (
                  <>
                <Box display="flex" alignItems="center" marginTop={1}>
                  <Person sx={{ marginRight: 1 }} />
                  <Typography variant="body2">
                    <strong>{t("courierName")}:</strong> {statusData.acceptedByName}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" marginTop={1}>
                  <Phone sx={{ marginRight: 1 }} />
                  <Typography variant="body2">
                    <strong>{t("courierPhone")}:</strong>
                    <Link
                      href={`tel:${statusData.courierPhone}`}
                      underline="none"
                      sx={{ marginLeft: 1, color: "primary.main" }}
                    >
                      {statusData.courierPhone}
                    </Link>
                  </Typography>
                </Box>
                </>)}
                <Box display="flex" alignItems="center" marginTop={1}>
                  <AccessTime sx={{ marginRight: 1 }} />
                  <Typography variant="body2">
                    <strong>{t("lastUpdated")}</strong>{" "}
                    {moment(statusData.updated).format("HH:mm")}
                  </Typography>
                </Box>
              </Paper>

              {/* Lower Paper with Delivery Details if Available */}
              {(statusData.status === "ongoing" ||
                statusData.status === "picked") && (
                <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("deliveryDetails")}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("pickupPlaceholder")}</strong> {statusData.from}
                  </Typography>
                  {statusData.deliveryType !== 'new-multi' && <Typography variant="body2">
                    <strong>{t("dropoffPlaceholder")}</strong> {statusData.to}
                  </Typography>}
                  {statusData.deliveryType !== 'new-multi' && <Typography variant="body2">
                    <strong>{t("deliveredBy")}</strong>{" "}
                    {moment(statusData.targetTime).format("HH:mm DD.MM")}
                  </Typography>}
                </Paper>
              )}

              {/* Display Google Map if coordinates are available */}
              {statusData.coordinates && (
                <Paper elevation={3} sx={{ padding: 2, marginTop: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("map")}
                  </Typography>
                  <Box sx={{ width: "100%", height: "300px", marginTop: 1 }}>
                    <GoogleMap
                      coordinates={statusData.coordinates}
                      courierLocation={{
                        latitude: statusData.latitude,
                        longitude: statusData.longitude,
                      }}
                    />
                  </Box>
                </Paper>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default DeliveryStatusPage;